import React, { useEffect } from "react";
import {
    Modal,
    Button,
    Row,
    Col,
    Form,
    Toast,
    OverlayTrigger,
    Tooltip,
    Accordion,
    Dropdown,
    DropdownButton
} from "react-bootstrap";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useRebarProjectInfoStore } from "../../../stores/rebarProjectInfoStore";
import { useTranslation } from "react-i18next";
import { createCp, createOptimizationInput } from "../../../services/cpService";
import { useNavigate } from "react-router-dom";
import InfoNoteText from "../../../components/InfoNote/InfoNoteText";
import {
    substitutionInfo,
    resolveFloorsSeparatelyInfo,
    minimumLengthInfo,
    variousSolversInfo,
    unoptimizedSolverInfo
} from "../../../infoContents";

const CuttingPlanCreationModal = ({
    projectId,
    show,
    onHide
}) => {

    const { i18n, t } = useTranslation();
    const navigate = useNavigate();

    const rebars = useRebarProjectInfoStore((state) => state.rebars);

    const selectedDiameters = Array.from(new Set(rebars.map((rebar) => rebar.diameter))).sort((a, b) => a - b);
    const selectedFloors = Array.from(new Set(rebars.map((rebar) => rebar.floor)));
    const selectedLayoutElements = Array.from(new Set(rebars.map((rebar) => rebar.element)));

    const lengthInterval = useRebarProjectInfoStore((state) => state.lengthInterval);
    const selectedExplanations = useRebarProjectInfoStore((state) => state.selectedExplanations);
    const weights = useRebarProjectInfoStore((state) => state.weights);

    const minValuesToHide = [Number.MIN_SAFE_INTEGER, ""];
    const maxValuesToHide = [Number.MAX_SAFE_INTEGER, ""];
    const showLengthInterval = !(minValuesToHide.includes(lengthInterval[0]) && maxValuesToHide.includes(lengthInterval[1]));
    const showExplanation = selectedExplanations.length > 0;
    const lengthIntervalStr = `${minValuesToHide.includes(lengthInterval[0]) ? "[0" : `[${lengthInterval[0]}`}, ${maxValuesToHide.includes(lengthInterval[1]) ? "∞)" : `${lengthInterval[1]}]`} cm`;

    const [cpName, setCpName] = React.useState("");
    const [useSubstitutes, setUseSubstitutes] = React.useState(false);
    const [separateFloors, setSeparateFloors] = React.useState(false);
    const [minBarLength, setMinBarLength] = React.useState(0);
    const [optimizationLevel, setOptimizationLevel] = React.useState(3);

    const [errorToast, setErrorToast] = React.useState({
        show: false,
        message: ""
    });
    const [isCpLoading, setIsCpLoading] = React.useState(false);

    async function handleSubmit(e) {
        e.preventDefault();

        setIsCpLoading(true)
        const optimizationInput = createOptimizationInput(
            rebars,
            useRebarProjectInfoStore.getState().rebarInformation,
            useRebarProjectInfoStore.getState().allFloors,
            separateFloors,
        );

        if (Object.keys(optimizationInput.inputDonatiDict).length === 0) {
            setErrorToast({ show: true, message: t("Optimize edilecek donatı bulunamadı.") })
            setIsCpLoading(false)
            return
        }

        try {
            const response = await createCp({
                projectType: "rebar",
                id: projectId,
                cpName: cpName,
                optimizationInput: optimizationInput,
                diameters: selectedDiameters.map((diameter) => diameter.toString()),
                useSubstitutes: useSubstitutes,
                solveFloorByFloor: separateFloors,
                minBarLength: minBarLength,
                selectedFloors: selectedFloors,
                optimizationLevel: optimizationLevel,
            });

            if (response.ok) {
                const data = await response.json();
                const cpId = data.id
                window.location = `/${i18n.language}/cp-info/${cpId}`
                await new Promise(r => setTimeout(r, 1000));
                setIsCpLoading(false);
            }
            else if (response.status === 400) {

                const data = await response.json();
                let errorDetail = data.detail
                const errorCode = data.hataKodu ? `(${t("Hata Kodu")} : ${data.hataKodu})` : ""
                setIsCpLoading(false);

                setErrorToast({
                    show: true,
                    message: `${errorDetail} ${errorCode}`,
                })
            }
            else {
                setIsCpLoading(false);

                setErrorToast({
                    show: true,
                    message: `Error. Try Again. CT9VPC`,
                })
            }
        }
        catch {
            var props = {
                error_code: "CT9VPC",
                detail: t("Sistem Hatası: Optimizasyon yapılamıyor. Sistem yöneticisi ile irtibata geçin."),
            }

            navigate(`/error`, { state: { props: props } })
        }
    }

    const optimizationLevelNames = {
        1: t("Hızlı 1"),
        2: t("Hızlı 2"),
        3: t("Standart"),
        4: t("Detaylı"),
    }

    let optimizationLevelDescription = "";
    if (optimizationLevel === 1) {
        optimizationLevelDescription = t("fast1Desc");
    } else if (optimizationLevel === 2) {
        optimizationLevelDescription = t("fast2Desc");
    } else if (optimizationLevel === 3) {
        optimizationLevelDescription = t("standardDesc");
    } else if (optimizationLevel === 4) {
        optimizationLevelDescription = t("detailedDesc");
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        // animation={false}
        >
            <Form onSubmit={(e) => handleSubmit(e)}>
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        {t("Kesim Planı Oluştur")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col xs={6} className="d-flex justify-content-start border-end">
                            <Row className="w-100">
                                <h5 >{t("Seçim Özeti")}</h5>
                                <i>{t("cutting_plan_creation_note")}</i>
                                <div className="mt-3">
                                    <span className="fw-bold">{t("Toplam Ağırlık")}:</span> {Object.values(weights).reduce(
                                        (acc, weight) => acc + weight, 0
                                    ).toLocaleString()} kg
                                </div>
                                <div className="mt-3">
                                    <span className="fw-bold">{t("Seçilen Katlar")}:</span> {selectedFloors.join(", ")}</div>
                                <div className="mt-3">
                                    <span className="fw-bold">{t("Seçilen Yapı Elemanları")}:</span> {selectedLayoutElements.join(", ")}
                                </div>
                                <div className="mt-3">
                                    <span className="fw-bold">{t("Seçilen Çaplar")}:</span> {selectedDiameters.map((diameter) => `Φ${diameter}`).join(", ")}
                                </div>
                                {showLengthInterval &&
                                    <div className="mt-3">
                                        <span className="fw-bold">{t("Uzunluk filtresi")}:</span> {lengthIntervalStr}
                                    </div>
                                }
                                {showExplanation &&
                                    <div className="mt-3">
                                        <span className="fw-bold">{t("Açıklama filtresi")}:</span> {selectedExplanations.join(", ")}
                                    </div>
                                }
                            </Row>
                        </Col>

                        <Col xs={6} className="d-flex flex-column justify-content-center align-items-start">
                            <Row className='py-4 border-bottom w-100'>
                                <Col md={4} className='d-flex align-items-center'>
                                    <span
                                        style={{
                                            fontSize: "1.0rem",
                                            color: "#555555"
                                        }}
                                    >
                                        {t("Kesim Planı ismi")}:
                                    </span>
                                </Col>
                                <Col md={8} className='d-flex align-items-center'>
                                    <Form.Control
                                        required
                                        type="text"
                                        maxLength={25}
                                        placeholder={t("Kesim Planı ismi giriniz")}
                                        onChange={(e) => setCpName(e.target.value)}
                                    />
                                </Col>
                            </Row>
                            <Row className='py-4 border-bottom w-100'>
                                <Col md={5} className='d-flex align-items-center'>
                                    <span
                                        style={{
                                            fontSize: "1.0rem",
                                            color: "#555555"
                                        }}
                                    >
                                        {t("Artıklar bir alt çap için kullanılsın")}:
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 0, hide: 0 }}
                                            overlay={(props) => (
                                                <Tooltip {...props} className="m-1">
                                                    {substitutionInfo[i18n.language]}
                                                </Tooltip>
                                            )}
                                        >
                                            <span>
                                                <InfoNoteText
                                                    className="ms-2"
                                                />
                                            </span>
                                        </OverlayTrigger>
                                    </span>

                                </Col>
                                <Col md={1} className='d-flex align-items-center border-end'>
                                    <Form.Check
                                        type="checkbox"
                                        style={{ transform: "scale(1.5)" }}
                                        onChange={(e) => setUseSubstitutes(e.target.checked)}
                                    />
                                </Col>
                                <Col md={5} className='d-flex align-items-center'>
                                    <span
                                        style={{
                                            fontSize: "1.0rem",
                                            color: "#555555"
                                        }}
                                    >
                                        {t("Seçilen katlar kendi arasında çözülsün")}:
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 0, hide: 0 }}
                                            overlay={(props) => (
                                                <Tooltip {...props} className="m-1">
                                                    {resolveFloorsSeparatelyInfo[i18n.language]}
                                                </Tooltip>
                                            )}
                                        >
                                            <span>
                                                <InfoNoteText
                                                    className="ms-2"
                                                />
                                            </span>
                                        </OverlayTrigger>
                                    </span>
                                </Col>
                                <Col md={1} className='d-flex align-items-center'>
                                    <Form.Check
                                        type="checkbox"
                                        style={{ transform: "scale(1.5)" }}
                                        onChange={(e) => setSeparateFloors(e.target.checked)}
                                    />
                                </Col>
                            </Row>
                            <Row className='py-4 w-100 border-bottom mb-4'>
                                <Col md={6} className='d-flex align-items-center'>
                                    <span
                                        style={{
                                            fontSize: "1.0rem",
                                            color: "#555555"
                                        }}
                                    >
                                        {t("Elde kalan fire için dikkate alınacak en kısa boy")} (cm):
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 0, hide: 0 }}
                                            overlay={(props) => (
                                                <Tooltip {...props} className="m-1">
                                                    {minimumLengthInfo[i18n.language]}
                                                </Tooltip>
                                            )}
                                        >
                                            <span>
                                                <InfoNoteText
                                                    className="ms-2"
                                                />
                                            </span>
                                        </OverlayTrigger>
                                    </span>
                                </Col>
                                <Col md={6} className='d-flex align-items-center'>
                                    <Form.Control
                                        required
                                        type="number"
                                        defaultValue={0}
                                        min={0}
                                        max={100}
                                        onChange={(e) => setMinBarLength(e.target.value)}
                                    />
                                </Col>
                            </Row>
                            <Accordion className="w-100 border-0">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>
                                        <span
                                            style={{
                                                fontSize: "1.0rem",
                                                color: "#555555"
                                            }}
                                        >
                                            {t("Diğer Optimizasyon Seçenekleri")}
                                        </span>
                                    </Accordion.Header>
                                    <Accordion.Body className="p-3">
                                        <Row className='pb-3'>
                                            <span
                                                style={{
                                                    fontSize: "1.0rem",
                                                    color: "#555555"
                                                }}
                                            >
                                                {t("Optimizasyon Seviyesi")}
                                            </span>
                                            <div className="mt-2">
                                                <Form.Select
                                                    aria-label="Optimization level"
                                                    defaultValue={3}
                                                    onChange={(e) => setOptimizationLevel(parseInt(e.target.value))}
                                                >
                                                    <option value={1}>🟢 {t("Hızlı 1")}</option>
                                                    <option value={2}>🔵 {t("Hızlı 2")}</option>
                                                    <option value={3}>🟠 {t("Standart")}</option>
                                                    <option value={4}>🔴 {t("Detaylı")}</option>
                                                </Form.Select>
                                            </div>
                                            <div style={{ minHeight: "5em", alignItems: "center", display: "flex" }}>
                                                <i className="mt-2" style={{ color: "#555555" }}>
                                                    <b>{optimizationLevelNames[optimizationLevel]}</b>:&nbsp;
                                                    {optimizationLevelDescription}
                                                </i>
                                            </div>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="danger" onClick={onHide}>{t("İptal")}</Button>
                    <Button variant="success" type="submit" className="fw-bold">{t("HESAPLA")}</Button>
                </Modal.Footer>
            </Form>
            <Backdrop
                sx={{
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,0.9)',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
                open={isCpLoading}
            >
                <p className="mt-2 text-center">{t("Optimizasyon yapılıyor. Bu biraz zaman alabilir, lütfen bekleyiniz. Sayfayı kapatmayınız.")}</p>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Toast
                onClose={() => setErrorToast({ show: false, message: '' })}
                show={errorToast.show}
                bg="danger"
                delay={6000}
                autohide
                style={{
                    position: 'fixed',
                    bottom: 20,
                    left: 20,
                    zIndex: 9999,
                }}
            >
                <Toast.Header>
                    <strong className="me-auto">{t("Hata")}</strong>
                </Toast.Header>
                <Toast.Body style={{ color: 'white' }}>{errorToast.message}</Toast.Body>
            </Toast>
        </Modal>
    )
}

export default CuttingPlanCreationModal;